/**
 * Similar Entities Panel Component
 * 
 * Shows entities with similar behavioral profiles using embedding similarity.
 * Can be used in:
 * - Pattern of Life Page: Find entities that behave like a selected entity
 * - Link Analysis Page: Discover behavioral clusters in the network
 * 
 * Features:
 * - Visual similarity scores with progress bars
 * - Quick stats comparison (routine, mobility, social scores)
 * - Click to select/highlight entities
 * - Anomaly detection integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Alert,
  Collapse,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Slider,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  People as SimilarIcon,
  Warning as AnomalyIcon,
  Psychology as AIIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Visibility as ViewIcon,
  Timeline as TimelineIcon,
  Home as RoutineIcon,
  DirectionsWalk as MobilityIcon,
  Hub as NetworkIcon,
  Fingerprint as BehaviorIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import polService, {
  SimilarEntity,
  AnomalySummary,
} from '../../services/polService';

interface SimilarEntitiesPanelProps {
  // Required props
  selectedEntityId: string | null;
  courseId: string;
  
  // Optional callbacks
  onEntitySelect?: (entityId: string) => void;
  onEntityHighlight?: (entityIds: string[]) => void;
  onCompareEntities?: (entityA: string, entityB: string) => void;
  
  // Optional configuration
  maxResults?: number;
  minSimilarity?: number;
  showAnomalyInfo?: boolean;
  compact?: boolean;
  title?: string;
}

const SimilarEntitiesPanel: React.FC<SimilarEntitiesPanelProps> = ({
  selectedEntityId,
  courseId,
  onEntitySelect,
  onEntityHighlight,
  onCompareEntities,
  maxResults = 10,
  minSimilarity = 0.3,
  showAnomalyInfo = true,
  compact = false,
  title = 'Similar Behavioral Profiles',
}) => {
  const theme = useTheme();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [similarEntities, setSimilarEntities] = useState<SimilarEntity[]>([]);
  const [expanded, setExpanded] = useState(true);
  const [similarityThreshold, setSimilarityThreshold] = useState(minSimilarity);
  const [anomalySummaries, setAnomalySummaries] = useState<Record<string, AnomalySummary>>({});
  const [loadingAnomalies, setLoadingAnomalies] = useState<Set<string>>(new Set());
  /** Why peer matching is unavailable — distinct from "entity has no POL data". */
  const [emptyReason, setEmptyReason] = useState<
    'no_summary' | 'no_embedding' | 'no_graph' | null
  >(null);
  const [similarityType, setSimilarityType] = useState<'behavioral' | 'network'>('behavioral');

  // Fetch similar entities when selection changes
  const fetchSimilarEntities = useCallback(async () => {
    if (!selectedEntityId || !courseId) {
      setSimilarEntities([]);
      return;
    }

    setLoading(true);
    setError(null);
    setEmptyReason(null);

    try {
      // Use different API based on similarity type
      const response = similarityType === 'behavioral'
        ? await polService.findSimilarEntities(
            selectedEntityId,
            courseId,
            maxResults,
            similarityThreshold
          )
        : await polService.findSimilarByNetwork(
            selectedEntityId,
            courseId,
            maxResults
          );

      if (response.message) {
        const msg = response.message;
        // Backend distinguishes missing POL summary vs missing similarity embedding —
        // keep those distinct so we don't claim "no POL" when the entity card has data.
        if (msg.includes('No graph embedding')) {
          setEmptyReason('no_graph');
          setError(null);
        } else if (msg.includes('No embedding')) {
          setEmptyReason('no_embedding');
          setError(null);
        } else if (msg.includes('No Pattern of Life')) {
          setEmptyReason('no_summary');
          setError(null);
        } else {
          setEmptyReason(null);
          setError(msg);
        }
        setSimilarEntities([]);
      } else {
        setEmptyReason(null);
        setError(null);
        // Map network_similarity to similarity for consistent display
        const entities = (response.similar_entities || []).map((e: any) => ({
          ...e,
          similarity: e.similarity ?? e.network_similarity ?? 0,
        }));
        setSimilarEntities(entities);
      }
    } catch (err: any) {
      if (err.response?.status === 501) {
        setError('Embedding service not available. Contact administrator to enable similarity search.');
      } else if (err.response?.status === 404) {
        setError('Entity not found in the selected course.');
      } else {
        setError(err.message || 'Failed to find similar entities');
      }
      setSimilarEntities([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, courseId, maxResults, similarityThreshold, similarityType]);

  useEffect(() => {
    fetchSimilarEntities();
  }, [fetchSimilarEntities]);

  // Refetch when similarity type changes
  useEffect(() => {
    if (selectedEntityId && courseId) {
      fetchSimilarEntities();
    }
  }, [similarityType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch anomaly summary for an entity
  const fetchAnomalySummary = async (entityId: string) => {
    if (anomalySummaries[entityId] || loadingAnomalies.has(entityId)) return;

    setLoadingAnomalies(prev => new Set(prev).add(entityId));

    try {
      const summary = await polService.getAnomalySummary(entityId, courseId, 30);
      setAnomalySummaries(prev => ({ ...prev, [entityId]: summary }));
    } catch (err) {
      // Silently fail - anomaly info is optional
      console.warn(`Failed to fetch anomaly summary for ${entityId}:`, err);
    } finally {
      setLoadingAnomalies(prev => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
    }
  };

  // Get similarity color based on score
  const getSimilarityColor = (similarity: number): string => {
    if (similarity >= 0.9) return theme.palette.success.main;
    if (similarity >= 0.7) return theme.palette.info.main;
    if (similarity >= 0.5) return theme.palette.warning.main;
    return theme.palette.grey[500];
  };

  // Get score label and color
  const getScoreInfo = (score: number, type: 'routine' | 'mobility' | 'social') => {
    const colors = {
      routine: { high: theme.palette.success.main, low: theme.palette.warning.main },
      mobility: { high: theme.palette.info.main, low: theme.palette.grey[500] },
      social: { high: theme.palette.secondary.main, low: theme.palette.grey[500] },
    };
    
    const labels = {
      routine: score > 0.7 ? 'Highly Routine' : score > 0.4 ? 'Moderate' : 'Variable',
      mobility: score > 0.7 ? 'High Mobility' : score > 0.4 ? 'Moderate' : 'Low Mobility',
      social: score > 0.5 ? 'Social' : 'Reserved',
    };

    return {
      label: labels[type],
      color: score > 0.5 ? colors[type].high : colors[type].low,
    };
  };

  // Highlight all similar entities on map/graph
  const handleHighlightAll = () => {
    if (onEntityHighlight && similarEntities.length > 0) {
      const entityIds = similarEntities.map(e => e.entity_id);
      if (selectedEntityId) entityIds.unshift(selectedEntityId);
      onEntityHighlight(entityIds);
    }
  };

  // Render empty state
  if (!selectedEntityId) {
    return (
      <Paper
        sx={{
          p: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ opacity: 0.7 }}>
          <SimilarIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Select an entity to find similar behavioral profiles
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        bgcolor: alpha(theme.palette.background.paper, 0.95),
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: compact ? 1.5 : 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AIIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {similarEntities.length > 0 && (
            <Chip
              label={similarEntities.length}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.75rem' }}
            />
          )}
        </Stack>
        
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchSimilarEntities} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {similarEntities.length > 0 && onEntityHighlight && (
            <Tooltip title="Highlight all similar entities">
              <IconButton size="small" onClick={handleHighlightAll}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        {/* Similarity Type Toggle & Threshold Slider */}
        {!compact && (
          <Box sx={{ px: 2, pt: 2 }}>
            {/* Toggle between Behavioral and Network similarity */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Similarity Type:
              </Typography>
              <ToggleButtonGroup
                value={similarityType}
                exclusive
                onChange={(_, value) => value && setSimilarityType(value)}
                size="small"
                sx={{ height: 28 }}
              >
                <ToggleButton value="behavioral" sx={{ px: 1.5, py: 0.5 }}>
                  <Tooltip title="Find peers with similar dwell / routine patterns (what they do)">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <BehaviorIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">Behavioral</Typography>
                    </Stack>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="network" sx={{ px: 1.5, py: 0.5 }}>
                  <Tooltip title="Find peers with a similar co-location / co-travel role (not phone contacts)">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <NetworkIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">Network</Typography>
                    </Stack>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            
            {/* Threshold slider - only for behavioral similarity */}
            {similarityType === 'behavioral' && (
              <>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Minimum Similarity: {Math.round(similarityThreshold * 100)}%
                </Typography>
                <Slider
                  value={similarityThreshold}
                  onChange={(_, value: any) => setSimilarityThreshold(value as number)}
                  onChangeCommitted={() => fetchSimilarEntities()}
                  min={0.1}
                  max={0.9}
                  step={0.05}
                  size="small"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                  sx={{ mt: 0.5 }}
                />
              </>
            )}
            
            {similarityType === 'network' && (
              <Alert severity="info" sx={{ py: 0.5, mt: 1 }} icon={<NetworkIcon fontSize="small" />}>
                <Typography variant="caption">
                  Compares co-location / co-travel network role — not phone or SMS contacts
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Finding similar entities...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="warning" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Similarity unavailable — message depends on *why*, not a blanket "no POL" */}
        {emptyReason && (
          <Alert 
            severity="info" 
            sx={{ m: 2 }}
          >
            {emptyReason === 'no_summary'
              ? 'No Pattern of Life summary for this entity yet. Peer matching needs a summary built from dwell history. Anomaly scoring can still appear once daily models run.'
              : emptyReason === 'no_embedding'
                ? 'Behavioral peer matching is not ready yet for this entity. Daily anomaly models may already be running — this only blocks “find similar behavior,” not Anomaly Activity below.'
                : 'Network peer matching is not ready yet. This needs co-location / co-travel links in the correlation graph — phone or SMS contacts alone are not enough. Anomaly Activity below is separate.'}
          </Alert>
        )}

        {/* Results */}
        {!loading && !error && !emptyReason && similarEntities.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No similar entities found above {Math.round(similarityThreshold * 100)}% similarity
            </Typography>
          </Box>
        )}

        {!loading && similarEntities.length > 0 && (
          <>
            {/* Warning if all similarities are suspiciously high */}
            {similarEntities.length > 0 && similarEntities.every((e: SimilarEntity) => e.similarity >= 0.99) && (
              <Alert severity="info" sx={{ m: 2, mb: 1 }}>
                All entities show ≥99% similarity. This may indicate identical behavioral patterns or incomplete data.
              </Alert>
            )}
            
            {/* Warning if all scores are zero */}
            {similarEntities.length > 0 && similarEntities.every((e: SimilarEntity) =>
              e.routine_score === 0 && e.mobility_score === 0 && e.unique_locations === 0
            ) && (
              <Alert severity="warning" sx={{ m: 2, mb: 1 }}>
                All similar entities show zero activity scores. This may indicate missing or unprocessed data.
              </Alert>
            )}
            
            <List dense sx={{ maxHeight: compact ? 300 : 400, overflow: 'auto' }}>
              {similarEntities.map((entity, index) => {
                const anomalySummary = anomalySummaries[entity.entity_id];
                const hasAnomaly = anomalySummary && anomalySummary.anomalous_days > 0;
                
                return (
                  <React.Fragment key={entity.entity_id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem
                      sx={{
                        py: 1.5,
                        cursor: onEntitySelect ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                      onClick={() => onEntitySelect?.(entity.entity_id)}
                      onMouseEnter={() => showAnomalyInfo && fetchAnomalySummary(entity.entity_id)}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: alpha(getSimilarityColor(entity.similarity), 0.2),
                            color: getSimilarityColor(entity.similarity),
                            fontSize: '0.9rem',
                            fontWeight: 600,
                          }}
                        >
                          {Math.round(entity.similarity * 100)}
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                        secondaryTypographyProps={{ component: 'div' } as any}
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={500}>
                              {entity.entity_id}
                            </Typography>
                            {hasAnomaly && (
                              <Tooltip title={`${anomalySummary.anomalous_days} anomalous days in last 30 days`}>
                                <AnomalyIcon 
                                  fontSize="small" 
                                  sx={{ color: theme.palette.warning.main, fontSize: 16 }} 
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        }
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            <Tooltip title={`Routine Score: ${Math.round(entity.routine_score * 100)}%`}>
                              <Chip
                                icon={<RoutineIcon sx={{ fontSize: 12 }} />}
                                label={Math.round(entity.routine_score * 100) + '%'}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(getScoreInfo(entity.routine_score, 'routine').color, 0.15),
                                  color: getScoreInfo(entity.routine_score, 'routine').color,
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={`Mobility Score: ${Math.round(entity.mobility_score * 100)}%`}>
                              <Chip
                                icon={<MobilityIcon sx={{ fontSize: 12 }} />}
                                label={Math.round(entity.mobility_score * 100) + '%'}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(getScoreInfo(entity.mobility_score, 'mobility').color, 0.15),
                                  color: getScoreInfo(entity.mobility_score, 'mobility').color,
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={`${entity.unique_locations} locations, ${entity.total_dwells} dwells`}>
                              <Chip
                                label={`${entity.unique_locations} loc`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                }}
                              />
                            </Tooltip>
                          </Stack>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={0.5}>
                          {onCompareEntities && selectedEntityId && (
                            <Tooltip title="Compare with selected entity">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCompareEntities(selectedEntityId, entity.entity_id);
                                }}
                              >
                                <TimelineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </>
        )}

        {/* Summary Stats */}
        {!loading && similarEntities.length > 0 && !compact && (
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
            <Typography variant="caption" color="text.secondary">
              Showing {similarEntities.length} entities with ≥{Math.round(similarityThreshold * 100)}% 
              behavioral similarity to {selectedEntityId}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default SimilarEntitiesPanel;

